import { AloftPrice, AloftPriceJSON } from '@aloft/shared-objects';
import { schAloftPrice } from '@aloft/toolkit-shared';
import { Input, theme } from '@aloft/toolkit-sui';

import { useState } from 'react';
import { Controller, ControllerRenderProps, FieldPath, useWatch } from 'react-hook-form';

import { selectCompAdjustments } from '../../../redux/features/selectors/adjustmentSelectors.ts';
import { useAppDispatch, useAppSelector } from '../../../redux/hooks.ts';
import { PropertyGridForm } from '../types.ts';
import { handleUpdatePropertyData } from '../utils.ts';
import { PropertyGridFieldProps } from './types.ts';
import { getAdjustmentColor, getCompAdjustmentValue } from './utils.tsx';

export const AdjustmentField = ({
  columnIndex,
  field,
  control,
  getValues,
}: PropertyGridFieldProps) => {
  const dispatch = useAppDispatch();
  const { manualAdjustmentKey } = field;

  // If a field doesn't have a manualAdjustmentKey, render a disabled input
  if (!manualAdjustmentKey) {
    return (
      <Input
        type="text"
        value=""
        isDisabled
        onChange={() => {}}
        onClear={() => {}}
        __dangerDoNotUseSx={{
          height: '100%',
          padding: '4px 8px',
          border: '1px solid transparent',
          outline: 'none',
          borderRadius: '0',
          fontSize: '14px',
          textAlign: 'right',
          backgroundColor: theme.colors.textOffset,
        }}
      />
    );
  }

  const fieldName =
    `columns.${columnIndex}.propertyData.editableData.manualAdjustments.${manualAdjustmentKey}` as FieldPath<PropertyGridForm>;

  const manualAdjustmentOverride = useWatch({ control, name: fieldName }) as
    | AloftPriceJSON
    | undefined;
  const propertyId = useWatch({ control, name: `columns.${columnIndex}.propertyId` });

  const compAdjustments = useAppSelector(selectCompAdjustments)[propertyId];
  let adjustmentValue = getCompAdjustmentValue(manualAdjustmentKey, compAdjustments);

  if (
    manualAdjustmentKey === 'functionalUtility' ||
    manualAdjustmentKey === 'externalObsolescence'
  ) {
    if (adjustmentValue.unitAmount > 0) {
      adjustmentValue = adjustmentValue.mul(-1);
    }
  }

  // Local state to track raw input value while typing
  const [rawInputValue, setRawInputValue] = useState<string | undefined>(undefined);
  const [isFocused, setIsFocused] = useState(false);

  const handleSetFieldValue = (onChange: ControllerRenderProps['onChange'], newValue?: string) => {
    const parsedNewValue = newValue ? AloftPrice.fromDollarLike(newValue) : undefined;

    if (parsedNewValue?.equals(adjustmentValue)) return;

    const parsedNewValueJSON = parsedNewValue?.toJSON();
    onChange(parsedNewValueJSON);
    const propertyData = getValues(`columns.${columnIndex}.propertyData`);
    handleUpdatePropertyData(propertyId, getValues(), dispatch, propertyData);
  };

  const color = getAdjustmentColor(adjustmentValue);

  return (
    <Controller
      name={fieldName}
      control={control}
      render={({ field: { onChange, ref } }) => {
        const displayValue = (
          manualAdjustmentOverride
            ? schAloftPrice.parse(manualAdjustmentOverride).toString(true)
            : adjustmentValue
        )?.toString(true);

        const handleFocus = () => {
          setIsFocused(true);
          // Remove $ symbol when focusing for easier editing (handles both "$100" and "-$100")
          const valueWithoutDollar =
            displayValue?.replace(/-?\$/, (match) => (match === '-$' ? '-' : '')) ?? '';
          setRawInputValue(valueWithoutDollar);
        };

        const handleBlur = () => {
          setIsFocused(false);

          // Use raw input value if available, otherwise use display value
          const inputToParse = rawInputValue !== undefined ? rawInputValue : displayValue;

          // Handle empty string or whitespace
          if (!inputToParse || inputToParse.trim() === '') {
            handleSetFieldValue(onChange, '$0');
            setRawInputValue('');
            return;
          }

          // Handle "-$0" case: auto-correct to "$0"
          const trimmedInput = inputToParse.trim();
          if (trimmedInput === '-$0' || trimmedInput === '-$' || trimmedInput === '-') {
            handleSetFieldValue(onChange, '$0');
            setRawInputValue(undefined);
            return;
          }

          // Try to parse the value
          try {
            const parsed = AloftPrice.fromDollarLike(inputToParse);
            // If parsed value is zero and input starts with '-', treat as zero
            if (parsed.equals(AloftPrice.zero) && trimmedInput.startsWith('-')) {
              handleSetFieldValue(onChange, '$0');
            } else {
              handleSetFieldValue(onChange, inputToParse);
            }
          } catch {
            // If parsing fails, try to recover
            // If it's just a minus sign or incomplete, set to zero
            if (trimmedInput === '-' || trimmedInput === '-$') {
              handleSetFieldValue(onChange, '$0');
            } else {
              // Otherwise, revert to display value
              handleSetFieldValue(onChange, displayValue);
            }
          }

          setRawInputValue(undefined);
        };

        const handleChange = (v: string) => {
          setRawInputValue(v.replace(/^0+/, ''));
        };

        const handleKeyDown = (e: React.KeyboardEvent) => {
          if (e.key === 'Escape') {
            setRawInputValue(undefined);
            setIsFocused(false);
            onChange(adjustmentValue?.toJSON() ?? undefined);
          } else if (e.key === 'Enter' || e.key === 'NumpadEnter') {
            (e.target as HTMLInputElement).blur();
          }
        };

        // Use raw input value when focused, otherwise use display value
        const inputValue =
          isFocused && rawInputValue !== undefined ? rawInputValue : (displayValue ?? '');

        return (
          <Input
            ref={ref}
            type="text"
            value={inputValue}
            __dangerDoNotUseSx={{
              height: '100%',
              padding: '4px 8px',
              border: manualAdjustmentOverride
                ? `1px dashed ${theme.colors.buttonDefault}`
                : '1px solid transparent',
              outline: 'none',
              borderRadius: '0',
              fontSize: '14px',
              textAlign: 'right',
              backgroundColor: theme.colors.textOffset,
              color,
              _focusVisible: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
                background: theme.colors.surface3,
              },
              _hover: {
                outline: '1.5px solid',
                outlineOffset: '-1.5px',
                outlineColor: theme.colors.buttonDefault,
              },
            }}
            onFocus={handleFocus}
            onBlur={handleBlur}
            onChange={handleChange}
            onClear={() => {
              setRawInputValue('');
              onChange(AloftPrice.zero.toJSON());
            }}
            onKeyDown={handleKeyDown}
          />
        );
      }}
    />
  );
};
